import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 基础件。数量刻意很少——每加一个通用组件都要在下游项目里被覆盖一遍样式，
 * 不如只做那些"每个项目都会重写、而且重写容易写砸"的。
 */
import { forwardRef } from 'react';
import { haptic } from '../hooks/gestures.js';
/**
 * 会给出**按压反馈**的按钮。
 *
 * 原生 App 的手感差别很大程度上就来自这一点：手指按下去的那一瞬间必须有视觉响应，
 * 而不是等 click 事件冒泡完再变。用 `:active` 而不是 onMouseDown 是因为 `:active`
 * 在触屏上是立即生效的，不用等浏览器判定这是不是滚动。
 */
export const Pressable = forwardRef(function Pressable({ scaleOnPress = true, hapticFeedback = false, className = '', onPointerDown, children, type = 'button', ...rest }, ref) {
    return (_jsx("button", { ref: ref, type: type, "data-ps-pressable": "", "data-ps-scale": scaleOnPress ? '' : undefined, className: `ps-pressable ${className}`.trim(), onPointerDown: (e) => {
            if (hapticFeedback)
                haptic();
            onPointerDown?.(e);
        }, ...rest, children: children }));
});
export function Button({ variant = 'secondary', size = 'md', loading = false, block = false, icon, disabled, className = '', children, ...rest }) {
    return (_jsxs(Pressable, { className: `ps-btn ps-btn-${variant} ps-btn-${size} ${block ? 'ps-btn-block' : ''} ${className}`.trim(), disabled: disabled || loading, "aria-busy": loading || undefined, ...rest, children: [loading && _jsx(Spinner, { size: size === 'lg' ? 18 : 15, className: "ps-btn-spinner" }), icon && !loading && _jsx("span", { className: "ps-btn-icon", children: icon }), _jsx("span", { className: "ps-btn-label", "data-ps-hidden": loading ? '' : undefined, children: children })] }));
}
/* ─────────────────────────── Spinner ─────────────────────────── */
export function Spinner({ size = 18, className = '' }) {
    return (_jsx("span", { className: `ps-spinner ${className}`.trim(), style: { width: size, height: size }, role: "status", "aria-label": "\u52A0\u8F7D\u4E2D" }));
}
/**
 * 骨架屏。
 *
 * ⚠️ 骨架屏是"内容马上就来"的承诺。**如果请求可能永远不返回（断网），必须有超时并
 * 切到错误态**——否则用户会永久停在骨架屏上，而这和白屏一样是"坏了但没人知道"。
 */
export function Skeleton({ width, height = 16, circle = false, lines = 1, className = '', style, ...rest }) {
    if (lines > 1) {
        return (_jsx("div", { className: `ps-skeleton-group ${className}`.trim(), ...rest, children: Array.from({ length: lines }, (_, i) => (_jsx("div", { className: "ps-skeleton", style: { height, width: i === lines - 1 ? '62%' : (width ?? '100%') } }, i))) }));
    }
    return (_jsx("div", { className: `ps-skeleton ${circle ? 'ps-skeleton-circle' : ''} ${className}`.trim(), style: { width: width ?? '100%', height, ...style }, ...rest }));
}
export function EmptyState({ icon, title, description, action, className = '' }) {
    return (_jsxs("div", { className: `ps-empty ${className}`.trim(), children: [icon && _jsx("div", { className: "ps-empty-icon", children: icon }), _jsx("p", { className: "ps-empty-title", children: title }), description && _jsx("p", { className: "ps-empty-desc", children: description }), action && _jsx("div", { className: "ps-empty-action", children: action })] }));
}
/**
 * 标准页面结构：顶栏固定 + 中间滚动 + 底栏固定。
 *
 * 中间那层用的是 `.ps-scroll`（`overscroll-behavior: contain` + `min-height: 0`）——
 * 这两条是"内容把容器撑破"和"滚到头把整个页面拽下来"的解药。
 */
export function Screen({ header, footer, children, className = '', ...rest }) {
    return (_jsxs("div", { className: `ps-screen ${className}`.trim(), ...rest, children: [header && _jsx("div", { className: "ps-screen-header ps-safe-top", children: header }), _jsx("div", { className: "ps-scroll ps-screen-body", children: children }), footer && _jsx("div", { className: "ps-screen-footer ps-safe-bottom", children: footer })] }));
}
//# sourceMappingURL=primitives.js.map